<template>
  <div class="font-sans max-w-screen-xl mx-auto py-10">
    <div class="flex justify-between items-center mb-5">
      <button @click="previousWeek"
              class="bg-blue/90 hover:bg-blue font-title uppercase text-[20px] sm:text-[24px] text-black border border-black px-4 py-2.5  cursor-pointer ">
        <span class="hidden sm:inline"><</span> {{ isMobileView ? 'Previous' : 'Previous Week' }}
      </button>
      <h2 class="font-title font-semibold text-[20px] hidden sm:block">{{ formatWeekRange }}</h2>
      <button @click="nextWeek"
              class="bg-blue/90 hover:bg-blue font-title uppercase text-[20px] sm:text-[24px] text-black border border-black px-4 py-2.5  cursor-pointer ">
        {{ isMobileView ? 'Next' : 'Next Week' }} <span class="hidden sm:inline">></span>
      </button>
    </div>

    <div class="flex mb-2.5 gap-x-1 ">
      <div v-for="day in weekDays" :key="day.date"
           :class="[
             'flex-1 font-title text-[18px] text-center p-2.5 border  border-gray-200',
             isPastDay(day.fullDate) || isFutureRestricted(day.fullDate) ? 'bg-gray-300 text-gray-500' : 'bg-gray-100'
           ]">
        <div class="font-bold">{{ day.name }}</div>
        <div class="font-swiza text-[15px]">{{ day.date }}</div>
      </div>
    </div>

    <div class="flex gap-1 font-sfizia">
      <div v-for="day in weekDays" :key="day.date" class="flex-1 gap-x-2 gap-y-1 flex flex-col">
        <div
            v-for="slot in timeSlots"
            :key="`${day.date}-${slot.time}`"
            class="h-10 border border-gray-300 flex items-center justify-center relative"
            :class="{
              'cursor-pointer': !isPastDay(day.fullDate) && !isFutureRestricted(day.fullDate) && slotAvailable(day.phpDate, slot.time) > 0,
              'cursor-not-allowed': isPastDay(day.fullDate) || isFutureRestricted(day.fullDate) || slotAvailable(day.phpDate, slot.time) == 0,
              'bg-blue-50 border-blue-300': isSlotSelected(day.date, slot.time),
              'text-white bg-green-800 hover:bg-green-900': (!isPastDay(day.fullDate) && !isFutureRestricted(day.fullDate) && slotAvailable(day.phpDate, slot.time) > 0 && slotAvailable(day.phpDate, slot.time) >= 5),
              'bg-yellow text-black': (!isPastDay(day.fullDate) && !isFutureRestricted(day.fullDate) && slotAvailable(day.phpDate, slot.time) > 0 && slotAvailable(day.phpDate, slot.time) < 5),
              'text-white bg-gray-500': (slotAvailable(day.phpDate, slot.time) == 0),
              'bg-gray-300 text-gray-500': isPastDay(day.fullDate) || isFutureRestricted(day.fullDate)
            }"
            @click="(!isPastDay(day.fullDate) && !isFutureRestricted(day.fullDate) && slotAvailable(day.phpDate, slot.time) > 0) ? openBookingModal(day.date, slot.time) : ''"
        >
          <span>{{ slot.time }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Booking Modal -->
  <Teleport to="body">
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
         @click="closeModal">
      <div class="bg-white p-4 w-full max-w-[calc(100%-40px)] sm:max-w-md mx-auto overflow-y-auto max-h-[calc(100vh-40px)]" @click.stop>
        <!-- Form State -->
        <div v-if="modalState === 'form'">
          <div
              class="font-title px-2 sm:px-3 mb-4 pt-3 sm:pt-4 pb-2 sm:pb-3  bg-yellow border border-black text-[24px] sm:text-[34px]  leading-none uppercase">
            Book slot
          </div>

          <h2 class="text-lg sm:text-xl font-bold mb-1">{{ selectedDate }} at {{ selectedTime }}</h2>
          <h3 class="text-md sm:text-lg mb-4">Space available: {{ slotAvailable(selectedPhpDate, selectedTime) }}</h3>

          <div class="mb-4">
            <label for="name" class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
                required
                type="text"
                id="name"
                v-model="bookingForm.name"
                @blur="nameFieldTouched = true"
                :class="[
                  'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue',
                  nameFieldTouched && bookingForm.name.trim() === '' ? 'border-red-500' : 'border-gray-300'
                ]"
            >
            <div v-if="nameFieldTouched && bookingForm.name.trim() === ''" class="text-red-500 text-xs mt-1">Name is
              required
            </div>
          </div>

          <div class="mb-4">
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
            <input
                required
                type="email"
                id="email"
                v-model="bookingForm.email"
                @blur="emailFieldTouched = true"
                :class="[
                  'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue',
                  emailFieldTouched && bookingForm.email && !isEmailValid ? 'border-red-500' : 'border-gray-300'
                ]"
            >
            <div v-if="emailFieldTouched && bookingForm.email && !isEmailValid" class="text-red-500 text-xs mt-1">Please
              enter a valid email address
            </div>
          </div>

          <div class="mb-4">
            <label for="groupSize" class="block text-sm font-medium text-gray-700 mb-1">Number of people (inc.
              children)</label>
            <input
                type="number"
                id="groupSize"
                v-model="bookingForm.groupSize"
                @blur="groupSizeTouched = true"
                min="1"
                :class="[
                  'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue',
                  groupSizeTouched && !isGroupSizeValid ? 'border-red-500' : 'border-gray-300'
                ]"
            >
            <div v-if="groupSizeTouched && !isGroupSizeValid" class="text-red-500 text-xs mt-1">
              Group size cannot exceed available slots ({{ slotAvailable(selectedPhpDate, selectedTime) }})
            </div>
          </div>

          <div class="mb-4">
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                    id="terms"
                    type="checkbox"
                    v-model="bookingForm.termsAccepted"
                    class="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-blue"
                    required
                >
              </div>
              <div class="ml-3 text-sm">
                <label for="terms" class="font-medium text-gray-700">I accept the <a class="underline"
                                                                                     href="/exhibition-terms-and-conditions">terms
                  and conditions</a> *</label>
              </div>
            </div>
          </div>

          <div class="mb-6">
            <div class="flex items-start">
              <div class="flex items-center h-5">
                <input
                    id="mailingList"
                    type="checkbox"
                    v-model="bookingForm.mailingList"
                    class="w-4 h-4 border border-gray-300 rounded focus:ring-2 focus:ring-blue"
                >
              </div>
              <div class="ml-3 text-sm">
                <label for="mailingList" class="font-medium text-gray-700">Sign up for our mailing list</label>
              </div>
            </div>
          </div>

          <div class="flex justify-end space-x-3">
            <button
                @click="closeModal"
                class="px-4 py-2 border border-gray-300  text-gray-700 bg-gray-300/80 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
                @click="recaptcha"
                :disabled="!isFormValid"
                :class="[
                  'font-title uppercase text-[24px] border border-black px-4 py-2 sm:py-2.5',
                  isFormValid 
                    ? 'bg-blue/90 hover:bg-blue text-black cursor-pointer' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]"
            >
              Book Now
            </button>
          </div>
        </div>
        <div v-if="errorMessage != ''" class="text-red-500 mt-1">
          {{ errorMessage }}
        </div>

        <!-- Booking State -->
        <div v-else-if="modalState === 'booking'" class="text-center py-8">
          <h2 class="text-xl font-bold mb-4">Booking Now...</h2>
          <p class="mb-4">Please wait while we process your booking.</p>
          <div class="flex justify-center">
            <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
          </div>
        </div>

        <!-- Success State -->
        <div v-else-if="modalState === 'success'" class="py-2">
          <div
              class="font-title px-3 mb-4 pt-4 pb-3  bg-yellow border border-black text-[34px]  leading-none uppercase">
            Thank you
          </div>
          <p class="mb-6">Your booking has been confirmed.</p>

          <div class=" mb-6 text-left">
            <p class="mb-2"><span class="font-semibold">Date:</span> {{ bookingResponse?.data?.date }}</p>
            <p class="mb-2"><span class="font-semibold">Time:</span> {{ bookingResponse?.data?.time }}</p>
            <p class="mb-2"><span class="font-semibold">Name:</span> {{ bookingResponse?.data?.name }}</p>
            <p class="mb-2"><span class="font-semibold">Email:</span> {{ bookingResponse?.data?.email }}</p>
            <p><span class="font-semibold">Group Size:</span> {{ bookingResponse?.data?.groupSize }}</p>
          </div>

          <div class="flex justify-end">
            <button
                @click="closeModal"
                class="bg-blue/90 hover:bg-blue font-title uppercase text-[24px] text-black border border-black px-4 py-2.5 rounded cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Teleport } from 'vue';
import { useReCaptcha } from 'vue-recaptcha-v3'


// Current week's start date (initialized in initializeCurrentWeekStart)
const currentWeekStart = ref(new Date());

// Detect mobile view
const isMobileView = ref(false);

const reCaptcha = useReCaptcha();

// Update mobile status on window resize
function updateMobileStatus() {
  isMobileView.value = window.innerWidth < 768;
}

// Initialize currentWeekStart based on mobile view
function initializeCurrentWeekStart() {
  if (isMobileView.value) {
    // On mobile, use current day unless it's Monday or Tuesday
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, 2 = Tuesday, etc.

    if (dayOfWeek === 1 || dayOfWeek === 2) {
      // If Monday or Tuesday, set to Wednesday (day 3)
      const wednesday = new Date(today);
      wednesday.setDate(today.getDate() + (3 - dayOfWeek)); // Adjust to Wednesday
      currentWeekStart.value = wednesday;
    } else {
      // Otherwise, use current day
      currentWeekStart.value = today;
    }
  } else {
    // On desktop, use Monday of current week
    currentWeekStart.value = getMonday(new Date());
  }
}

// Modal state
const showModal = ref(false);
const modalState = ref('form'); // 'form', 'booking', 'success'
const selectedDate = ref('');
const selectedPhpDate = ref(''); // For PHP-readable format
const selectedTime = ref('');
const bookingForm = ref({
  name: '',
  email: '',
  groupSize: 1,
  termsAccepted: false,
  mailingList: true
});
const bookingResponse = ref(null);

// Booked slots tracking
const bookedSlots = ref([]);
const errorMessage = ref('');

// Form validation and field touch tracking
const nameFieldTouched = ref(false);
const emailFieldTouched = ref(false);
const groupSizeTouched = ref(false);

const isNameValid = computed(() => bookingForm.value.name.trim() !== '');
const isEmailValid = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(bookingForm.value.email);
});
const isGroupSizeValid = computed(() => {
  const availableSlots = slotAvailable(selectedPhpDate.value, selectedTime.value);
  return bookingForm.value.groupSize > 0 && bookingForm.value.groupSize <= availableSlots;
});
const isFormValid = computed(() => isNameValid.value && isEmailValid.value && isGroupSizeValid.value && bookingForm.value.termsAccepted);

// Get Monday of the current week
function getMonday(date) {
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(date.setDate(diff));
}

// Format date as '22 May 2025'
function formatDate(date) {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${date.getDate()} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
}

// Format date as YYYY-MM-DD for PHP
function formatDateForPHP(date) {
  return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
}

// Navigation functions - navigate by 2 days on mobile, 7 days on desktop
function previousWeek() {
  // Update mobile status before navigation to ensure we have the latest value
  updateMobileStatus();

  const newDate = new Date(currentWeekStart.value);
  // On mobile, navigate by 2 days, on desktop navigate by 7 days
  const daysToNavigate = isMobileView.value ? 2 : 7;
  newDate.setDate(newDate.getDate() - daysToNavigate);
  currentWeekStart.value = newDate;
}

function nextWeek() {
  // Update mobile status before navigation to ensure we have the latest value
  updateMobileStatus();

  const newDate = new Date(currentWeekStart.value);
  console.log(newDate);
  // On mobile, navigate by 2 days, on desktop navigate by 7 days
  const daysToNavigate = isMobileView.value ? 2 : 7;
  newDate.setDate(newDate.getDate() + daysToNavigate);
  currentWeekStart.value = newDate;
}

// Generate weekdays for the current week
const weekDays = computed(() => {
  const days = [];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  // Start from the current week start date (Monday)
  const startDate = new Date(currentWeekStart.value);

  // Generate 7 days starting from the current week start date
  for (let i = 0; i < 7; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);

    // Skip Mondays (1) and Tuesdays (2)
    const dayOfWeek = date.getDay();
    if (dayOfWeek !== 1 && dayOfWeek !== 2) {
      days.push({
        name: dayNames[dayOfWeek],
        date: formatDate(date),
        phpDate: formatDateForPHP(date),
        fullDate: new Date(date)
      });
    }
  }

  // On mobile, only show the first 2 days (after skipping Mondays and Tuesdays)
  return isMobileView.value ? days.slice(0, 2) : days;
});

// Format week range for display (e.g., "22 May 2025 - 28 May 2025")
const formatWeekRange = computed(() => {
  // Use the actual days from weekDays computed property
  const days = weekDays.value;

  if (days.length === 0) {
    return '';
  }

  // Get the first and last day from the filtered days
  const startDate = days[0].fullDate;
  const endDate = days[days.length - 1].fullDate;

  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
});

// Generate time slots (26 slots of 15 minutes each, starting at 9:15 AM with 5-minute gaps)
const timeSlots = computed(() => {
  const slots = [];
  let hour = 9;
  let minute = 15;

  for (let i = 0; i < 25; i++) {
    // Format time as HH:MM
    const formattedHour = hour.toString().padStart(2, '0');
    const formattedMinute = minute.toString().padStart(2, '0');
    const time = `${formattedHour}:${formattedMinute}`;

    slots.push({time});

    // Increment time by 15 minutes + 5 minutes gap
    minute += 20;
    if (minute >= 60) {
      hour += Math.floor(minute / 60);
      minute %= 60;
    }
  }

  return slots;
});

// Selected slots tracking
const selectedSlots = ref([]);

// Check if a slot is selected
function isSlotSelected(date, time) {
  return selectedSlots.value.some(slot => slot.date === date && slot.time === time);
}

// Check if a day is in the past (yesterday or before)
function isPastDay(dayDate) {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Set to beginning of today

  const checkDate = new Date(dayDate);
  checkDate.setHours(0, 0, 0, 0); // Set to beginning of the day

  return checkDate < today;
}

// Check if a day is after the cutoff date (June 29th, 2025)
function isFutureRestricted(dayDate) {
  const cutoffDate = new Date(2025, 6, 27); // June is month 5 (0-indexed)
  cutoffDate.setHours(23, 59, 59, 999); // Set to end of the cutoff day

  const checkDate = new Date(dayDate);
  checkDate.setHours(0, 0, 0, 0); // Set to beginning of the day

  return checkDate > cutoffDate;
}

// Check if a slot has bookings
function slotAvailable(date, time) {
  // Create a date object for the slot we're checking
  const [hours, minutes] = time.split(':').map(Number);

  // Check each booking
  for (const slot of bookedSlots.value) {
    // Normalize the date format
    let slotDate = slot.date;
    if (slotDate && typeof slotDate === 'string' && !slotDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
      try {
        const dateObj = new Date(slotDate);
        slotDate = formatDateForPHP(dateObj);
      } catch (e) {
        console.error('Error converting date:', e);
        continue; // Skip this slot if date conversion fails
      }
    }

    // Extract hours and minutes from the slot time (handling HH:MM:SS format)
    const slotTimeParts = slot.time.split(':');
    const slotHours = parseInt(slotTimeParts[0], 10);
    const slotMinutes = parseInt(slotTimeParts[1], 10);
    // Compare date and time (hours and minutes only)
    if (slotDate === date && slotHours === hours && slotMinutes === minutes) {
      return slot.available;
    }
  }

  return 15;
}

// Toggle slot selection
function toggleSlotSelection(date, time) {
  const slotIndex = selectedSlots.value.findIndex(slot => slot.date === date && slot.time === time);

  if (slotIndex === -1) {
    // Add to selected slots
    selectedSlots.value.push({date, time});
  } else {
    // Remove from selected slots
    selectedSlots.value.splice(slotIndex, 1);
  }
}

// Load bookings from the server
function loadBookings() {
  fetch('/actions/museum/booking/get-bookings', {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    }
  })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        if (data.success) {
          bookedSlots.value = data.bookings;
          console.log('Loaded bookings:', bookedSlots.value);
        }
      })
      .catch(error => {
        console.error('Error loading bookings:', error);
      });
}

// Open booking modal
function openBookingModal(date, time) {
  console.log('Opening booking modal for', date, time);
  // Find the corresponding day to get the PHP-formatted date
  const day = weekDays.value.find(d => d.date === date);

  // Check if the date is after the cutoff date
  if (day && isFutureRestricted(day.fullDate)) {
    console.log('Cannot book after June 29th, 2025');
    return;
  }

  // Set selectedDate with day of the week included
  selectedDate.value = day ? `${day.name} ${date}` : date;
  selectedPhpDate.value = day ? day.phpDate : '';
  selectedTime.value = time;
  showModal.value = true;

  // Reset modal state to form
  modalState.value = 'form';

  // Reset form
  bookingForm.value = {
    name: '',
    email: '',
    groupSize: 1,
    termsAccepted: false,
    mailingList: true
  };

  // Reset field touch state
  nameFieldTouched.value = false;
  emailFieldTouched.value = false;
  groupSizeTouched.value = false;
}

// Close booking modal
function closeModal() {
  showModal.value = false;
  // Reset modal state to form for next opening
  modalState.value = 'form';

  // Reset field touch state
  nameFieldTouched.value = false;
  emailFieldTouched.value = false;
  groupSizeTouched.value = false;
}


const recaptcha = async () => {
  modalState.value = 'booking';
  // (optional) Wait until recaptcha has been loaded.
  await reCaptcha.recaptchaLoaded();


  // Execute reCAPTCHA with action "login".
  const token = await reCaptcha.executeRecaptcha('login');

  const data = {
    token: token, // Display date (22 May 2025)
  };

  // DO NOT MODIFY CSRF IMPLEMENTATION
  data[csrfTokenName] = csrfTokenValue;

  // Do stuff with the received token.
  console.log(token);
  // Send the token to your server for verification
  fetch('/actions/museum/booking/verify-recaptcha', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: JSON.stringify(data)
  })
      .then(response => response.json())
      .then(data => {
        console.log('reCAPTCHA verification response:', data);
        // Handle the response from your server
        if (data.success) {
          bookNow();
        } else {
          errorMessage.value = 'There was an error processing your booking, your submission did not pass the Recaptcha. Please refresh the page and try again.';
          // Reset modal state to form
          modalState.value = 'form';
        }
      })
      .catch(error => {
        console.error('Error verifying reCAPTCHA:', error);
      });
}


// Handle booking
function bookNow() {
  // Set modal state to booking

  // Check if the selected date is after the cutoff date
  const selectedDateObj = new Date(selectedPhpDate.value);
  if (isFutureRestricted(selectedDateObj)) {
    console.error('Cannot book after June 29th, 2025');
    errorMessage.value = 'Booking is not available after June 29th, 2025.';
    modalState.value = 'form';
    return;
  }

  // Prepare booking data
  const bookingData = {
    date: selectedDate.value, // Display date (22 May 2025)
    phpDate: selectedPhpDate.value, // PHP-readable date (YYYY-MM-DD)
    time: selectedTime.value,
    ...bookingForm.value
  };

  // DO NOT MODIFY CSRF IMPLEMENTATION
  bookingData[csrfTokenName] = csrfTokenValue;

  // Log the booking data (for debugging)
  console.log('Booking:', bookingData);

  // Make a fetch call to the Craft CMS controller
  fetch('/actions/museum/booking/create', {
    method: 'POST',
    headers: {
      'Content-type': 'application/json',
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': getCsrfToken()
    },
    body: JSON.stringify(bookingData)
  })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        console.log('Booking successful:', data);

        // Store the response data
        bookingResponse.value = data;


        // Add to selected slots
        toggleSlotSelection(selectedDate.value, selectedTime.value);

        if (data.success) {
          // Set modal state to success
          modalState.value = 'success';
        } else {
          // Handle error response
          errorMessage.value = data.error || 'There was an error processing your booking. Please try again.';
          // Reset modal state to form
          modalState.value = 'form';
        }

        // Reload bookings to update the display
        loadBookings();
      })
      .catch(error => {
        console.error('Error booking slot:', error);
        // Here you could show an error message to the user
        // alert('There was an error processing your booking. Please try again.');
        errorMessage.value = 'There was an error processing your booking. Please try again.';
        // Reset modal state to form
        modalState.value = 'form';
      });
}

// Get CSRF token from meta tag
function getCsrfToken() {
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  return csrfToken || '';
}

// Handle keydown events for Escape key
function handleKeyDown(event) {
  if (event.key === 'Escape' && showModal.value) {
    closeModal();
  }
}

// Load bookings when component is mounted and set up event listeners
onMounted(() => {
  loadBookings();

  // Add event listener for Escape key
  window.addEventListener('keydown', handleKeyDown);

  // Check mobile status on mount
  updateMobileStatus();

  // Initialize currentWeekStart based on mobile view
  initializeCurrentWeekStart();

  // Add event listener for window resize
  window.addEventListener('resize', updateMobileStatus);
});

// Clean up event listeners when component is unmounted
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('resize', updateMobileStatus);
});
</script>
